import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';

const FadeUp = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 1, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function About() {
  const BOOKING_URL = "https://link.nordiccrm.ca/widget/bookings/dorman-ernest-baltazar-personal-calendar-qg2bbc8wa";

  return (
    <div className="w-full bg-kin-black">
      <section className="relative min-h-[80vh] flex items-center justify-center px-6 pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 right-0 w-[50vw] h-full bg-gradient-to-l from-kin-gold/5 to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto text-center flex flex-col items-center">
          <FadeUp>
            <p className="text-kin-gold tracking-[0.4em] text-xs uppercase font-light mb-8"> Founder • Creator • Strategist </p>
          </FadeUp>
          <FadeUp delay={0.2}>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif text-balance leading-tight text-kin-ivory"> Helping Creators Build Lives That <span className="italic">Remain Whole</span> Under Pressure. </h1>
          </FadeUp>
          <FadeUp delay={0.4} className="mt-12">
            <div className="w-px h-24 bg-gradient-to-b from-kin-gold/40 to-transparent"></div>
          </FadeUp>
        </div>
      </section>

      <section className="py-32 md:py-56 px-6 bg-[#09090b]">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-20 lg:gap-32">
          <div className="lg:w-1/2">
            <FadeUp className="relative">
              <div className="aspect-[4/5] w-full overflow-hidden grayscale">
                <img src="https://media-manager-c.questera.ai/greta-media/9c5adfa6bba615730f4558013336717062b18ab46d11ed47c31c88c932e61ffbf11018f89f2243f6aef891f7c9f9eda4/images/aW1hZ2UvanBlZw==/e3a999aa4bef11412c7c81e9adfb1276.jpeg" alt="Dorman Baltazar" className="w-full h-full object-cover opacity-70 mix-blend-luminosity transform scale-110" />
              </div>
              <div className="absolute -top-6 -right-6 w-32 h-32 border-t border-r border-kin-gold/20"></div>
              <div className="absolute -bottom-10 -left-10 text-[10vw] font-serif text-kin-gold/5 pointer-events-none select-none"> BALTAZAR </div>
            </FadeUp>
          </div>
          <div className="lg:w-1/2 space-y-16">
            <FadeUp delay={0.2}>
              <h2 className="text-3xl md:text-5xl font-serif text-kin-ivory leading-tight"> The Philosophy Behind <br/> <span className="italic text-kin-gold">Kingdom Inspira Nova Studio</span> </h2>
            </FadeUp>
            <FadeUp delay={0.3}>
              <div className="space-y-8 text-lg md:text-xl text-kin-ivory/60 font-light leading-loose tracking-wide">
                <p> Kingdom Inspira Nova Studio was born from a simple realization: <span className="text-kin-ivory font-normal block mt-4 italic"> Many creators are building externally successful lives while internally collapsing under pressure. </span> </p>
                <div className="py-8">
                  <p className="text-xs uppercase tracking-[0.3em] text-kin-gold/50 mb-6 font-semibold">Modern creator culture rewards:</p>
                  <ul className="grid grid-cols-2 gap-4">
                    {['speed','visibility','performance','constant output'].map((item,i)=> (
                      <li key={i} className="flex items-center gap-3 text-kin-ivory/40 italic">
                        <span className="w-1.5 h-1.5 rounded-full bg-kin-ivory/10"></span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <p> But very few people are taught how to build sustainably. This ecosystem was created to help entrepreneurs, creators, and leaders develop aligned systems rooted in stewardship, identity, wellness, and sustainability. </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* DISCOVERY CTA (NEW) */}
      <section className="py-32 md:py-48 px-6 border-t border-kin-ivory/5 bg-kin-black text-center">
        <div className="max-w-4xl mx-auto">
          <FadeUp>
            <h2 className="text-3xl md:text-5xl font-serif text-kin-ivory mb-12">
              Interested In <span className="italic text-kin-gold">Working Together?</span>
            </h2>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button variant="primary" className="px-16 py-6 text-sm"> Book A Discovery Call </Button>
            </a>
          </FadeUp>
        </div>
      </section>

      <section className="py-40 md:py-60 px-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(198,160,98,0.03)_0%,_transparent_70%)]"></div>
        <div className="max-w-4xl mx-auto relative z-10 flex flex-col items-center">
          <FadeUp>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-serif text-kin-ivory leading-tight mb-16"> The Future Belongs To <br/> <span className="italic text-kin-gold">Sustainable Creators.</span> </h2>
          </FadeUp>
          <FadeUp delay={0.2} className="w-full flex justify-center">
            <Link to="/">
              <Button variant="primary" className="px-16 py-6 text-sm"> Explore The Ecosystem </Button>
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}